import { ArdoqId, ViewIds } from '@ardoq/api-types';
import type { APIReferenceType } from '@ardoq/api-types';
import {
  ComponentDetails,
  ComponentModelData,
  DependencyWheelViewModel,
  DependencyWheelViewSettings,
  ReferenceDetails,
  ReferenceModelData,
  collectReferenceDetails,
  filterReferenceToRenderIds,
  getComponentsToDisplay,
  getConnectedComponentIds,
  getReferenceIdsByComponentId,
} from '@ardoq/dependency-wheel';
import { DEPENDENCY_WHEEL_COMPONENTS_LIMIT } from 'views/environmentals/viewDataLimits';
import { componentInterface } from '@ardoq/component-interface';
import { referenceInterface } from '@ardoq/reference-interface';
import { workspaceInterface } from '@ardoq/workspace-interface';
import { getComponentLabelParts } from '@ardoq/graph';
import { isTruthy } from '@ardoq/common-helpers';
import { uniq, uniqBy } from 'lodash';
import type { SimpleReferenceModel, ContextShape } from '@ardoq/data-model';
import { getDefaultViewSettings } from 'views/environmentals/viewSettings/getDefaultViewSettings';

const resolveComponentModelData = (
  componentId: ArdoqId
): ComponentModelData => {
  const workspaceId = componentInterface.getWorkspaceId(componentId)!;
  const component = componentInterface.get(componentId);
  const componentType = workspaceInterface.getTypeById(
    workspaceId,
    component!.typeId
  );
  const { fill: backgroundColor, stroke: color } =
    componentInterface.getComponentDisplayColorAsSVGAttributes(componentId, {
      useAsBackgroundStyle: true,
    });

  return {
    id: componentId,
    labelInfo: getComponentLabelParts(componentId),
    iconId: component?.icon ?? componentType?.icon ?? undefined,
    imageSrc: componentInterface.getImage(componentId) ?? undefined,
    shapeId: componentInterface.getShape(componentId) ?? undefined,
    backgroundColor,
    color,
  };
};

const resolveReferenceModelData = (
  referenceId: ArdoqId
): ReferenceModelData => {
  const reference = referenceInterface.get(referenceId);
  const referenceType =
    reference && reference.rootWorkspace
      ? workspaceInterface.getReferenceTypeById(
          reference.rootWorkspace,
          reference.type
        )
      : null;
  const cssColors = referenceInterface.getCssColors(referenceId, {
    useAsBackgroundStyle: false,
  });

  return { referenceId, reference, referenceType, cssColors };
};

type WheelDataToDisplay = {
  references: ReferenceDetails[];
  components: ComponentDetails[];
  isComponentsCountLimitExceeded: boolean;
  totalComponentsCount: number;
};

const getDataToDisplay = (
  componentId: ArdoqId,
  referenceMap: Map<string, SimpleReferenceModel>,
  subtreeIds: ArdoqId[],
  workspaceComponentIds: ArdoqId[]
): WheelDataToDisplay => {
  const { componentIds, componentsCount } = getComponentsToDisplayData(
    referenceMap,
    subtreeIds,
    workspaceComponentIds,
    componentId
  );

  const references = getReferencesToDisplayData(
    referenceMap,
    componentIds,
    subtreeIds,
    componentId
  );

  const components = getComponentsToDisplay(
    componentIds.map(resolveComponentModelData)
  );

  return {
    references,
    components,
    isComponentsCountLimitExceeded:
      componentsCount > DEPENDENCY_WHEEL_COMPONENTS_LIMIT,
    totalComponentsCount: componentsCount,
  };
};

const getReferencesToDisplayData = (
  referenceMap: Map<string, SimpleReferenceModel>,
  componentToDisplayIds: ArdoqId[],
  subtreeIds: ArdoqId[],
  selectedComponentId?: ArdoqId
) => {
  const componentToDisplayIdsSet = new Set<ArdoqId>(componentToDisplayIds);

  const selectedComponentAndDescendantsIds = selectedComponentId && [
    selectedComponentId,
    ...subtreeIds,
  ];

  const referenceIds = Array.isArray(selectedComponentAndDescendantsIds)
    ? getReferenceIdsToDisplay(
        selectedComponentAndDescendantsIds,
        componentToDisplayIdsSet,
        referenceMap
      )
    : Array.from(referenceMap.keys());

  const referenceToDisplayIds = filterReferenceToRenderIds({
    referenceIds,
    referenceMap,
    componentToDisplayIdsSet,
    selectedComponentId,
  });

  return uniq(referenceToDisplayIds)
    .map(resolveReferenceModelData)
    .map(collectReferenceDetails)
    .filter(isTruthy);
};

const getComponentsToDisplayData = (
  referenceMap: Map<string, SimpleReferenceModel>,
  subtreeIds: ArdoqId[],
  workspaceComponentIds: ArdoqId[],
  componentId?: ArdoqId
) => {
  const componentIds = componentId
    ? [componentId, ...subtreeIds]
    : workspaceComponentIds;

  const connectedComponentIds = getConnectedComponentIds(
    componentIds,
    referenceMap
  );

  return {
    componentsCount: connectedComponentIds.length,
    componentIds: connectedComponentIds.slice(
      0,
      DEPENDENCY_WHEEL_COMPONENTS_LIMIT
    ),
  };
};

const getReferenceIdsToDisplay = (
  selectedComponentAndDescendantsIds: ArdoqId[],
  visibleComponentIds: Set<ArdoqId>,
  referenceMap: Map<string, SimpleReferenceModel>
) =>
  selectedComponentAndDescendantsIds.reduce<ArdoqId[]>(
    (acc, componentId) => [
      ...acc,
      ...getReferenceIdsByComponentId(componentId, referenceMap).filter(
        referenceId => {
          const ref = referenceMap.get(referenceId);
          return (
            ref !== undefined &&
            visibleComponentIds.has(ref.sourceId) &&
            visibleComponentIds.has(ref.targetId)
          );
        }
      ),
    ],
    []
  );

const getReferenceTypesForLegend = (
  references: ReferenceDetails[],
  referenceTypeMap: Map<ArdoqId, APIReferenceType>
) => {
  return uniqBy(
    references
      .map(reference => referenceTypeMap.get(reference.referenceId) ?? null)
      .filter(isTruthy),
    model => model.id
  );
};

export const EMPTY_DEPENDENCY_WHEEL = {
  viewSettings: getDefaultViewSettings(ViewIds.DEPWHEEL, false),
  references: [],
  components: [],
  isComponentsCountLimitExceeded: false,
  totalComponentsCount: 0,
  referenceTypesForLegend: [],
  selectedComponentId: null,
  isExploreMode: false,
} satisfies DependencyWheelViewModel;

export const composeViewModel = (
  context: ContextShape,
  viewSettings: DependencyWheelViewSettings,
  referenceMap: Map<string, SimpleReferenceModel>,
  subtreeIds: ArdoqId[],
  workspaceComponentIds: ArdoqId[],
  referenceTypeMap: Map<ArdoqId, APIReferenceType>
) => {
  const { componentId, isExploreMode, workspaceId } = context;
  if (!workspaceId) {
    return EMPTY_DEPENDENCY_WHEEL;
  }

  const {
    references,
    components,
    isComponentsCountLimitExceeded,
    totalComponentsCount,
  } = getDataToDisplay(
    componentId,
    referenceMap,
    subtreeIds,
    workspaceComponentIds
  );

  return {
    viewSettings,
    references,
    components,
    isExploreMode,
    isComponentsCountLimitExceeded,
    totalComponentsCount,
    referenceTypesForLegend: getReferenceTypesForLegend(
      references,
      referenceTypeMap
    ),
    selectedComponentId: componentId,
  };
};
